<template>
	<div>
		<div class="stage">
			<div class="mb-4 flex items-center lg:sticky lg:top-[50px] lg:z-[10]">
				<search-filters
					ref="searchFilters"
					:tenants="tenants"
					:accounts="accounts"
					:zone-names="zoneNames"
					:initial-dates="dates"
					:parent-params="searchParams"
					:carriers="carriers"
					:max-date-range="30"
					:loading="loading"
					@apply-filters="handleFilterChange"
					@clear-filters="handleClearFilters"
					@export="exportToCsv"
				/>
			</div>
			<div v-if="hasActiveFilters" class="row flex-wrap items-center justify-center py-5 pt-2 xs:pb-5 md:pt-4">
				<filter-pills
					:search-params="searchParams"
					:dates="dates"
					:tenants="tenants"
					:accounts="accounts"
					@clear-dates="clearDates"
					@remove-pill="removeFilterPill"
				/>
			</div>
			<div class="row between-md between-lg middle-md middle-lg">
				<div class="col-xs-12 col-sm-4">
					<div class="flex items-center gap-4">
						<p class="textUppercase textSml">
							Calls
							<span class="fontHeavy textGreen textSml"
								>{{ totalCalls }}{{ totalCalls === 10000 ? '+' : '' }}</span
							>
						</p>
						<button
							class="flex items-center gap-2 rounded-md px-3 py-1.5 text-sm text-gray-600 transition-all hover:-translate-y-0.5 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
							title="Export results to CSV"
							@click="exportToCsv"
						>
							<span class="fa-light fa-download text-base"></span>
							<span class="hidden sm:inline">Export</span>
						</button>
					</div>
				</div>
				<div v-if="!loading" class="col-sm-8 mb-4 ml-auto">
					<Pagination
						:model-value="pageSize"
						:current-page="currentPage"
						:total-pages="pages"
						:page-sizes="[25, 50, 100]"
						@update:model-value="changePageSize"
						@page-change="changePage"
					/>
				</div>
			</div>
			<div class="horizontalBreaker"></div>
			<div class="row margin-b20">
				<transition-fade easing="ease-in">
					<div v-show="loading" class="col-xs-12 col-sm-12 col-md-12 col-lg-12">
						<div v-show="loading" class="row margin-b20 center-xs center-md center-lg">
							<div class="loader"></div>
						</div>
					</div>
				</transition-fade>
				<transition-fade easing="ease-in">
					<calls-table v-show="!loading" :calls="calls"></calls-table>
				</transition-fade>
			</div>
			<transition-fade easing="ease-in">
				<div v-if="!loading && pages.length > 1" class="flex justify-end">
					<Pagination
						:model-value="pageSize"
						:current-page="currentPage"
						:total-pages="pages"
						:page-sizes="[25, 50, 100]"
						@update:model-value="changePageSize"
						@page-change="changePage"
					/>
				</div>
			</transition-fade>
		</div>
	</div>
</template>

<script>
import { router } from '../router'
import cdrsApi from '../scripts/calldetailrecords/calldetailrecords.api'
import phoneNumbersApi from '../scripts/phonenumbers/phonenumbers.api'
import reportsApi from '../scripts/reports/reports.api'
import moment from 'moment'
import { ref } from 'vue'
import carriersApi from '../scripts/carriers/carriers.api'
import tariffcodesApi from '@/scripts/tariffcodes/tariffcodes.api'
import accountsApi from '@/scripts/accounts/accounts.api'
import SearchFilters from '@/components/SearchFilters.vue'
import FilterPills from '@/components/FilterPills.vue'
import Pagination from '@/components/common/Pagination.vue'

export default {
	name: 'SearchCalls',
	components: {
		SearchFilters,
		FilterPills,
		Pagination
	},
	inject: ['user', 'userRoles', 'tenants', 'userAccounts'],

	setup() {
		const date = ref(new Date())
		const dp = ref()

		const selectDate = () => {
			dp.value.selectDate()
		}
		const closeMenu = () => {
			dp.value.closeMenu()
		}

		return {
			date,
			dp,
			selectDate,
			closeMenu
		}
	},
	data() {
		return {
			error: '',
			dates: [moment().subtract(30, 'days').toDate(), null],
			calls: [],
			totalCalls: 0,
			allSelected: false,
			pageSize: 25,
			currentPage: 1,
			accounts: [],
			loading: false,
			carriers: [],
			searchParams: {
				dateAddedFrom: moment().subtract(30, 'days').format('YYYY-MM-DD'),
				dateAddedTo: null,
				selectedTenant: null,
				selectedAccount: null,
				carrier: null,
				carrierName: null,
				callDurationFrom: null,
				callDurationTo: null,
				billableDurationFrom: null,
				billableDurationTo: null,
				callFrom: '',
				callTo: '',
				callAnywhere: '',
				zoneNames: [],
				hangupCodes: [],
				teleflowId: null,
				withRecording: null,
				withTranscription: null,
				withVoicemail: null
			},
			screenSize: '100%',
			screenWidth: 1024,
			zoneNames: [],
			hover: null,
			isInitialisingSearchParams: true,
			isInitialisingFromUrl: true,
			presetActive: false,
			activePreset: null
		}
	},

	computed: {
		hasActiveFilters() {
			return (
				this.searchParams.dateAddedFrom ||
				this.searchParams.dateAddedTo ||
				(this.searchParams.selectedTenant && Object.keys(this.searchParams.selectedTenant).length) ||
				(this.searchParams.selectedAccount && Object.keys(this.searchParams.selectedAccount).length) ||
				this.searchParams.carrier ||
				this.searchParams.callDurationFrom ||
				this.searchParams.callDurationTo ||
				this.searchParams.billableDurationFrom ||
				this.searchParams.billableDurationTo ||
				(this.searchParams.zoneNames && this.searchParams.zoneNames.length > 0) ||
				this.searchParams.callFrom ||
				this.searchParams.callTo ||
				this.searchParams.callAnywhere ||
				(this.searchParams.hangupCodes && this.searchParams.hangupCodes.length > 0) ||
				this.searchParams.teleflowId ||
				this.searchParams.withRecording ||
				this.searchParams.withTranscription ||
				this.searchParams.withVoicemail
			)
		},
		tenantCarriers() {
			return this.carriers
				.filter(
					(c) => !this.searchParams?.selectedTenant || c.tenant?.id === this.searchParams.selectedTenant.id
				)
				.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
		},
		tenantAccounts() {
			return this.accounts
				.filter(
					(a) => !this.searchParams?.selectedTenant || a.tenant?.id === this.searchParams.selectedTenant.id
				)
				.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
		},
		pages() {
			let numPages = Math.ceil(this.totalCalls / this.pageSize) || 1
			return Array.apply(null, { length: numPages }).map(Number.call, Number)
		},
		rowKeys() {
			if (this.reportData && this.reportData.length)
				return Object.keys(this.reportData[0]).filter((key) => key !== 'Tenant' || this.tenants?.length > 1)
			else return []
		},
		zonePlaceholderText() {
			if (this.selectedZoneNames.length) {
				return this.selectedZoneNames.join(', ')
			} else {
				return 'Search for zones'
			}
		},
		hangupCodePlaceholderText() {
			if (this.selectedHangupCodes.length) {
				return this.selectedHangupCodes.map((h) => `${h.code}`).join(', ')
			} else {
				return 'Search for hangup codes'
			}
		},
		filteredZoneNames() {
			const searchZoneLower = this.searchZone.toLowerCase()
			if (searchZoneLower) {
				return this.zoneNames.filter(
					(zone) => !this.selectedZoneNames.includes(zone) && zone.toLowerCase().includes(searchZoneLower)
				)
			}
			return this.zoneNames.filter((zone) => !this.selectedZoneNames.includes(zone))
		},
		filteredHangupCodes() {
			const allHangupCodes = []
			Object.keys(this.statusCodes).forEach((key) => {
				if (key !== 'Common') {
					allHangupCodes.push(...this.statusCodes[key])
				}
			})
			if (this.searchHangupCode) {
				return allHangupCodes.filter(
					(h) => !this.selectedHangupCodes.includes(h) && h.code.toString().includes(this.searchHangupCode)
				)
			}
			return allHangupCodes.filter((h) => !this.selectedHangupCodes.includes(h))
		},
		filteredColumns() {
			return this.selectedColumns.length
				? Object.keys(this.reportData[0])
						.filter((key) => !this.selectedColumns.includes(key))
						.filter((key) => key !== 'Tenant' || this.tenants?.length > 1)
				: Object.keys(this.reportData[0]).filter((key) => key !== 'Tenant' || this.tenants?.length > 1)
		}
	},

	watch: {
		dates: {
			handler: function (val) {
				if (val?.length === 2) {
					if (val[0]) {
						this.searchParams.dateAddedFrom = moment(val[0]).format('YYYY-MM-DD')
					} else {
						this.searchParams.dateAddedFrom = null
					}

					if (val[1]) {
						this.searchParams.dateAddedTo = moment(val[1]).format('YYYY-MM-DD')
					} else {
						this.searchParams.dateAddedTo = null
					}

					if (!this.isInitialisingFromUrl && !this.isInitialisingSearchParams) {
						if (this.presetActive) {
							this.removePresetFromUrl()
							this.presetActive = false
							this.activePreset = null
						}
						this.updateUrlParams()
					}
				}
			},
			deep: true
		},
		screenWidth: {
			handler(newValue) {
				this.checkScreenSize(newValue)
			}
		},
		searchParams: {
			deep: true,
			handler(newValue, oldValue) {
				if (this.isInitialisingSearchParams) {
					this.isInitialisingSearchParams = false
					return
				}
			}
		}
	},

	async created() {
		this.dates = [moment().subtract(30, 'days').toDate(), null]
		this.searchParams.dateAddedFrom = moment().subtract(30, 'days').format('YYYY-MM-DD')

		const urlSearchParams = new URLSearchParams(window.location.search)
		const urlParams = Object.fromEntries(urlSearchParams.entries())

		this.isInitialisingFromUrl = true
		this.isInitialisingSearchParams = true

		let presetActive = false
		if (urlParams.preset) {
			let start, end
			const now = new Date()
			if (urlParams.preset === 'last24h') {
				end = now
				start = new Date(now.getTime() - 24 * 60 * 60 * 1000)
			} else if (urlParams.preset === 'lastWeek') {
				end = now
				start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
			} else if (urlParams.preset === 'last30d') {
				end = now
				start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
			} else if (urlParams.preset === 'thisMonth') {
				start = new Date(now.getFullYear(), now.getMonth(), 1)
				end = now
			} else if (urlParams.preset === 'lastMonth') {
				start = new Date(now.getFullYear(), now.getMonth() - 1, 1)
				end = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999)
			}
			if (start && end) {
				this.dates = [start, end]
				this.searchParams.dateAddedFrom = moment(start).format('YYYY-MM-DD')
				this.searchParams.dateAddedTo = moment(end).format('YYYY-MM-DD')
				this.activePreset = urlParams.preset
				presetActive = true
			}
		}

		if (Object.keys(urlParams).length > 0) {
			if (!presetActive && (urlParams.dateFrom || urlParams.dateTo)) {
				const dateFrom = urlParams.dateFrom
				const dateTo = urlParams.dateTo
				this.dates = [dateFrom ? new Date(dateFrom) : null, dateTo ? new Date(dateTo) : null].filter(Boolean)
				this.searchParams.dateAddedFrom = dateFrom || null
				this.searchParams.dateAddedTo = dateTo || null
			}

			if (urlParams.teleflow_id) {
				this.searchParams.teleflowId = urlParams.teleflow_id || null
			}
		}

		this.loading = true
		this.presetActive = presetActive
	},

	async mounted() {
		window.addEventListener('resize', this.handleResize)

		window.addEventListener(
			'click',
			function (e) {
				if (!e.target.parentElement?.classList?.contains('closeOnMe')) {
					this.close()
				}
			}.bind(this),
			true
		)

		this.loadInitialData()
	},

	beforeUnmount() {
		window.removeEventListener('resize', this.handleResize)
	},

	methods: {
		async loadInitialData() {
			try {
				const urlSearchParams = new URLSearchParams(window.location.search)
				const urlParams = Object.fromEntries(urlSearchParams.entries())
				let shouldReloadData = false

				if (!this.userRoles?.includes('account')) {
					const [carriersResponse, accountsResponse] = await Promise.all([
						carriersApi.getAll(),
						accountsApi.getAll({ limit: 2500 })
					])
					this.carriers = carriersResponse.data.results
					this.accounts = accountsResponse.data.results

					if (urlParams.carrier && this.searchParams.carrier) {
						const carrierObj = this.carriers.find((c) => c.id.toString() === urlParams.carrier.toString())
						if (carrierObj) {
							this.searchParams.carrierName = carrierObj.name
						}
					}
				}

				const zoneResponse = await tariffcodesApi.getAllZoneNames()
				this.zoneNames = zoneResponse.data?.results || []

				if (urlParams.zones) {
					const zoneNames = urlParams.zones.split(',').map((zone) => decodeURIComponent(zone.trim()))
					this.searchParams.zoneNames = zoneNames
				}

				if (urlParams.tenant) {
					const tenant = this.tenants.find((t) => t.id === parseInt(urlParams.tenant))
					if (tenant) {
						this.searchParams.selectedTenant = tenant
						this.$global.setSelectedTenant({ target: { value: tenant.id } })
					}
				} else if (this.tenants?.length === 1 && !this.searchParams.teleflowId) {
					this.searchParams.selectedTenant = this.tenants[0]
				}

				if (urlParams.account && this.accounts?.length > 0) {
					const account = this.accounts.find((a) => a.id === parseInt(urlParams.account))
					if (account) {
						this.searchParams.selectedAccount = account
					}
				}

				if (urlParams.carrier) {
					this.searchParams.carrier = urlParams.carrier
				}

				if (urlParams.callFrom) {
					this.searchParams.callFrom = urlParams.callFrom
				}

				if (urlParams.callTo) {
					this.searchParams.callTo = urlParams.callTo
				}

				if (urlParams.callAnywhere) {
					this.searchParams.callAnywhere = urlParams.callAnywhere
				}

				if (urlParams.hangupCodes) {
					this.searchParams.hangupCodes = urlParams.hangupCodes
						.split(',')
						.map((code) => parseInt(code.trim()))
				}

				if (urlParams.durationFrom) {
					this.searchParams.callDurationFrom = parseInt(urlParams.durationFrom)
				}

				if (urlParams.durationTo) {
					this.searchParams.callDurationTo = parseInt(urlParams.durationTo)
				}

				if (urlParams.billableDurationFrom) {
					this.searchParams.billableDurationFrom = parseInt(urlParams.billableDurationFrom)
				}

				if (urlParams.billableDurationTo) {
					this.searchParams.billableDurationTo = parseInt(urlParams.billableDurationTo)
				}

				if (urlParams.withRecording) {
					this.searchParams.withRecording =
						urlParams.withRecording === 'true' || urlParams.withRecording === true
				}

				if (urlParams.withTranscription) {
					this.searchParams.withTranscription =
						urlParams.withTranscription === 'true' || urlParams.withTranscription === true
				}

				if (urlParams.withVoicemail) {
					this.searchParams.withVoicemail =
						urlParams.withVoicemail === 'true' || urlParams.withVoicemail === true
				}

				this.isInitialisingSearchParams = false

				await this.$nextTick()
				const searchFilters = this.$refs.searchFilters
				if (searchFilters) {
					searchFilters.initialiseFromParams(this.searchParams)
				}

				this.reloadCalls()

				setTimeout(() => {
					this.isInitialisingFromUrl = false
					this.updateUrlParams()
				}, 100)
			} catch (error) {
				console.error('Initialisation error:', error)
				this.isInitialisingSearchParams = false
				this.isInitialisingFromUrl = false
				this.loading = false
			}
		},

		reloadCalls() {
			this.loading = true
			this.totalCalls = 0

			let params = this.getSearchParams()

			cdrsApi
				.getAllCombined(params)
				.then((response) => {
					this.calls = response.data.results
					this.totalCalls = response.data.count

					const maxPages = Math.ceil(this.totalCalls / this.pageSize) || 1
					if (this.currentPage > maxPages) {
						this.currentPage = 1
						this.reloadCalls()
						return
					}

					this.loading = false
				})
				.catch((err) => {
					console.error('Error loading calls:', err)
					this.loading = false
				})
		},
		getDay(num) {
			num = parseInt(num)
			switch (num) {
				case 1:
					return 'Sun'
				case 2:
					return 'Mon'
				case 3:
					return 'Tue'
				case 4:
					return 'Wed'
				case 5:
					return 'Thu'
				case 6:
					return 'Fri'
				case 7:
					return 'Sat'
			}
		},
		changePageSize(size) {
			this.pageSize = size
			let numPages = Math.ceil(this.totalCalls / size) || 1

			if (numPages < this.currentPage) this.currentPage = numPages
			this.reloadCalls()
		},
		changePage(page) {
			this.currentPage = page
			this.reloadCalls()
		},
		routeTo(routeName) {
			router.push(routeName)
		},
		checkScreenSize(width) {
			if (width >= 1024) this.screenSize = this.getColumnWidth(this.selectedColumns?.length)
			else this.screenSize = '100%'
		},
		setAdvancedParams() {
			this.searchParams.callDurationFrom = parseInt(this.durationMinFilter) || null
			this.searchParams.callDurationTo = parseInt(this.durationMaxFilter) || null
			this.searchParams.billableDurationFrom = parseInt(this.billableMinFilter) || null
			this.searchParams.billableDurationTo = parseInt(this.billableMaxFilter) || null
			if (this.searchSource) this.searchParams.callFrom = this.searchSource
			if (this.searchDest) this.searchParams.callTo = this.searchDest
			if (this.selectedZoneNames?.length) this.searchParams.zoneNames = [...this.selectedZoneNames]
			if (this.selectedHangupCodes?.length)
				this.searchParams.hangupCodes = this.selectedHangupCodes.map((h) => h.code)
		},
		availableHangupCodes(codeGroup) {
			return this.filteredHangupCodes.filter((h) => this.statusCodes[codeGroup].find((cg) => cg.code === h.code))
		},
		clearDates() {
			this.dates = []
			this.searchParams.dateAddedFrom = null
			this.searchParams.dateAddedTo = null

			this.updateUrlParams()
		},
		log(txt) {
			console.log(txt)
			return true
		},
		resetSearchParams() {
			this.searchParams = {
				dateAddedFrom: moment().subtract(30, 'days').format('YYYY-MM-DD'),
				dateAddedTo: null,
				carrier: null,
				selectedTenant: null,
				selectedAccount: null,
				callFrom: '',
				callTo: '',
				hangupCodes: [],
				zoneNames: [],
				callDurationFrom: 0,
				callDurationTo: 0,
				billableDurationFrom: 0,
				billableDurationTo: 0,
				withRecording: null,
				withTranscription: null,
				withVoicemail: null
			}
			this.dates = [moment().subtract(30, 'days').toDate(), null]
			this.selectedHangupCodes = []
			this.selectedZoneNames = []
			this.searchSource = ''
			this.searchDest = ''
			this.durationMinFilter = ''
			this.durationMaxFilter = ''
			this.billableMinFilter = ''
			this.billableMaxFilter = ''
			this.setAdvancedParams()
			this.updateUrlParams()
			this.pills = []
		},
		getSearchParams() {
			let params = {
				limit: this.pageSize,
				skip: this.pageSize * (this.currentPage - 1)
			}

			if (this.searchParams) {
				if (this.searchParams.dateAddedFrom) {
					params.dateAddedFrom = this.searchParams.dateAddedFrom
				}

				if (this.searchParams.dateAddedTo) {
					params.dateAddedTo = this.searchParams.dateAddedTo
				}

				if (this.searchParams.selectedTenant && Object.keys(this.searchParams.selectedTenant).length) {
					params.selectedTenant = this.searchParams.selectedTenant
				}

				if (this.searchParams.selectedAccount && Object.keys(this.searchParams.selectedAccount).length) {
					params.selectedAccount = this.searchParams.selectedAccount
				}

				if (this.searchParams.carrier) {
					params.carrier = { id: this.searchParams.carrier }
				}

				if (this.searchParams.callDurationFrom !== null && this.searchParams.callDurationFrom !== undefined) {
					params.callDurationFrom = this.searchParams.callDurationFrom
				}

				if (this.searchParams.callDurationTo !== null && this.searchParams.callDurationTo !== undefined) {
					params.callDurationTo = this.searchParams.callDurationTo
				}

				if (
					this.searchParams.billableDurationFrom !== null &&
					this.searchParams.billableDurationFrom !== undefined
				) {
					params.billableDurationFrom = this.searchParams.billableDurationFrom
				}

				if (
					this.searchParams.billableDurationTo !== null &&
					this.searchParams.billableDurationTo !== undefined
				) {
					params.billableDurationTo = this.searchParams.billableDurationTo
				}

				if (this.searchParams.callFrom) {
					params.callFrom = this.searchParams.callFrom
				}

				if (this.searchParams.callTo) {
					params.callTo = this.searchParams.callTo
				}

				if (this.searchParams.callAnywhere) {
					params.callAnywhere = this.searchParams.callAnywhere
				}

				if (this.searchParams.zoneNames && this.searchParams.zoneNames.length > 0) {
					params.zoneNames = [...this.searchParams.zoneNames]
				}

				if (this.searchParams.hangupCodes && this.searchParams.hangupCodes.length > 0) {
					params.hangupCodes = [...this.searchParams.hangupCodes]
				}

				if (this.searchParams.teleflowId) {
					params.teleflowId = this.searchParams.teleflowId
				}

				if (this.searchParams.withRecording === true) {
					params.withRecording = true
				}
				if (this.searchParams.withTranscription === true) {
					params.withTranscription = true
				}
				if (this.searchParams.withVoicemail === true) {
					params.withVoicemail = true
				}
			}

			return params
		},
		hideTenantDropdown() {
			this.tenantDropdownVisible = false
		},
		hideZoneDropdown() {
			this.zoneDropdownVisible = false
			this.searchZone = ''
		},
		hideHangupCodeDropdown() {
			this.hangupCodeDropdownVisible = false
			this.searchHangupCode = ''
		},
		toggleZoneSelection(zoneName) {
			const index = this.selectedZoneNames.indexOf(zoneName)
			if (index >= 0) {
				this.selectedZoneNames.splice(index, 1)
			} else {
				this.selectedZoneNames.push(zoneName)
			}
		},
		toggleColumn(column) {
			const index = this.selectedColumns.indexOf(column)
			if (index >= 0) {
				this.selectedColumns.splice(index, 1)
			} else {
				this.selectedColumns.push(column)
			}
			this.checkScreenSize(this.screenWidth)
		},
		toggleHangupCodeSelection(hangupCode) {
			const index = this.selectedHangupCodes.indexOf(hangupCode)
			if (index >= 0) {
				this.selectedHangupCodes.splice(index, 1)
			} else {
				this.selectedHangupCodes.push(hangupCode)
			}
		},
		toggleSelectAllCalls() {
			this.allSelected = !this.allSelected
			for (let i = 0; i < this.contacts.length; i++) {
				this.contacts[i].selected = this.allSelected
			}
		},
		exportToCsv() {
			let params = this.getSearchParams()

			cdrsApi
				.getAllCombinedAsCsv(params)
				.then((response) => {
					let blob = new Blob([response.data], { type: 'text/csv' })
					let link = document.createElement('a')
					link.href = window.URL.createObjectURL(blob)
					link.download = `TeleFlow search calls.csv`
					link.click()
				})
				.catch((err) => {
					console.error(err)
				})
		},
		getColumnWidth(numberOfColumns) {
			const percentage = 100 / numberOfColumns
			return `${percentage}%`
		},
		handleResize() {
			this.screenWidth = window.innerWidth
		},
		close() {
			this.reportMenu = false
		},
		addFilterPill(name, category) {
			const pill = { name, category }
			this.pills.push(pill)
		},

		setTenant(tenant) {
			this.$global.setSelectedTenant({ target: { value: tenant?.id || '' } })
			this.searchParams.selectedTenant = tenant
			this.searchParams.selectedAccount = null
			this.searchParams.carrier = null

			const accountPillIndex = this.pills.findIndex((p) => p.category === 'Account')
			if (accountPillIndex > -1) {
				this.pills.splice(accountPillIndex, 1)
			}
		},

		clearSelectedTenant() {
			this.searchParams.selectedTenant = null
			this.setAdvancedParams()
			this.updateUrlParams()
		},

		updatePills() {
			this.pills = []

			if (this.searchParams.selectedTenant) {
				this.addFilterPill(this.searchParams.selectedTenant.companyName, 'Tenant')
			}

			if (this.searchParams.selectedAccount) {
				this.addFilterPill(this.searchParams.selectedAccount.name, 'Account')
			}

			if (this.searchDest) {
				this.addFilterPill(this.searchDest, 'Destination')
			}

			if (this.searchSource) {
				this.addFilterPill(this.searchSource, 'Source')
			}

			if (this.selectedHangupCodes?.length) {
				this.selectedHangupCodes.forEach((code) => {
					this.addFilterPill(code.code.toString(), 'Hangup Code')
				})
			}

			if (this.selectedZoneNames?.length) {
				this.selectedZoneNames.forEach((zone) => {
					this.addFilterPill(zone, 'Zone')
				})
			}

			if (this.durationMinFilter || this.durationMaxFilter) {
				this.addFilterPill(`${this.durationMinFilter || '0'} - ${this.durationMaxFilter || '∞'}s`, 'Duration')
			}

			if (this.billableMinFilter || this.billableMaxFilter) {
				this.addFilterPill(
					`${this.billableMinFilter || '0'} - ${this.billableMaxFilter || '∞'}s`,
					'Billable Duration'
				)
			}
		},
		handleFilterChange(filters) {
			if (this.isInitialisingFromUrl) {
				return
			}

			const oldParams = { ...this.searchParams }
			const mergedParams = { ...oldParams }

			Object.keys(filters).forEach((key) => {
				if (filters[key] !== undefined) {
					if (key === 'carrier' && typeof filters[key] === 'object' && filters[key] !== null) {
						mergedParams[key] = filters[key].id
						mergedParams.carrierName = filters[key].name
					} else if (key === 'preset') {
						this.presetActive = true
						this.activePreset = filters[key]
					} else {
						mergedParams[key] = filters[key]
					}
				}
			})

			if (filters.withRecording !== undefined) {
				mergedParams.withRecording = filters.withRecording === true
			}
			if (filters.withTranscription !== undefined) {
				mergedParams.withTranscription = filters.withTranscription === true
			}
			if (filters.withVoicemail !== undefined) {
				mergedParams.withVoicemail = filters.withVoicemail === true
			}

			this.searchParams = mergedParams

			if (filters.preset || filters.isSearching) {
				this.updateUrlParams()
			}

			if (filters.isSearching) {
				this.currentPage = 1
				this.reloadCalls()
			}
		},
		handleClearFilters() {
			const currentDateFrom = this.searchParams.dateAddedFrom
			const currentDateTo = this.searchParams.dateAddedTo
			const currentDates = [...this.dates]

			this.searchParams = {
				dateAddedFrom: currentDateFrom,
				dateAddedTo: currentDateTo,
				selectedTenant: null,
				selectedAccount: null,
				carrier: null,
				carrierName: null,
				callDurationFrom: null,
				callDurationTo: null,
				billableDurationFrom: null,
				billableDurationTo: null,
				callFrom: '',
				callTo: '',
				callAnywhere: '',
				zoneNames: [],
				hangupCodes: [],
				teleflowId: null,
				withRecording: null,
				withTranscription: null,
				withVoicemail: null
			}

			this.dates = currentDates
			this.currentPage = 1

			const params = new URLSearchParams()
			if (!this.presetActive) {
				if (currentDateFrom) {
					params.set('dateFrom', currentDateFrom)
				}
				if (currentDateTo) {
					params.set('dateTo', currentDateTo)
				}
			} else {
				const url = new URL(window.location.href)
				if (url.searchParams.has('preset')) {
					params.set('preset', url.searchParams.get('preset'))
				}
			}
			window.history.replaceState(
				{},
				'',
				window.location.pathname + (params.toString() ? '?' + params.toString() : '')
			)
		},
		updateUrlParams() {
			const params = new URLSearchParams()

			if (!this.presetActive) {
				if (this.searchParams.dateAddedFrom) {
					params.set('dateFrom', this.searchParams.dateAddedFrom)
				}
				if (this.searchParams.dateAddedTo) {
					params.set('dateTo', this.searchParams.dateAddedTo)
				}
			}
			if (this.searchParams.selectedTenant?.id) {
				params.set('tenant', this.searchParams.selectedTenant.id)
			}
			if (this.searchParams.selectedAccount?.id) {
				params.set('account', this.searchParams.selectedAccount.id)
			}
			if (this.searchParams.carrier) {
				params.set('carrier', this.searchParams.carrier)
			}
			if (this.searchParams.callDurationFrom !== null && this.searchParams.callDurationFrom !== undefined) {
				params.set('durationFrom', this.searchParams.callDurationFrom)
			}
			if (this.searchParams.callDurationTo !== null && this.searchParams.callDurationTo !== undefined) {
				params.set('durationTo', this.searchParams.callDurationTo)
			}
			if (
				this.searchParams.billableDurationFrom !== null &&
				this.searchParams.billableDurationFrom !== undefined
			) {
				params.set('billableDurationFrom', this.searchParams.billableDurationFrom)
			}
			if (this.searchParams.billableDurationTo !== null && this.searchParams.billableDurationTo !== undefined) {
				params.set('billableDurationTo', this.searchParams.billableDurationTo)
			}
			if (this.searchParams.callFrom) {
				params.set('callFrom', this.searchParams.callFrom)
			}
			if (this.searchParams.callTo) {
				params.set('callTo', this.searchParams.callTo)
			}
			if (this.searchParams.callAnywhere) {
				params.set('callAnywhere', this.searchParams.callAnywhere)
			}
			if (this.searchParams.hangupCodes?.length) {
				params.set('hangupCodes', this.searchParams.hangupCodes.join(','))
			}
			if (this.searchParams.zoneNames?.length) {
				params.set('zones', this.searchParams.zoneNames.join(','))
			}
			if (this.searchParams.teleflowId) {
				params.set('teleflow_id', this.searchParams.teleflowId)
				this.searchParams.selectedTenant = null
				this.searchParams.selectedAccount = null
				this.$global.setSelectedTenant({ target: { value: '' } })
			}

			if (this.presetActive && this.activePreset) {
				params.set('preset', this.activePreset)
			}

			if (this.searchParams.withRecording) {
				params.set('withRecording', this.searchParams.withRecording)
			}

			if (this.searchParams.withTranscription) {
				params.set('withTranscription', this.searchParams.withTranscription)
			}

			if (this.searchParams.withVoicemail) {
				params.set('withVoicemail', this.searchParams.withVoicemail)
			}

			const url = `${window.location.pathname}?${params.toString()}`
			window.history.replaceState({}, '', url)
		},
		formatDate(date) {
			return date ? moment(date).format('DD/MM/YYYY') : 'now'
		},
		removeFilterPill(pill) {
			console.log('removeFilterPill', pill)
			const filterType = pill.category.toLowerCase()

			switch (filterType) {
				case 'date':
					this.dates = []
					this.searchParams.dateAddedFrom = null
					this.searchParams.dateAddedTo = null
					break
				case 'tenant':
					this.searchParams.selectedTenant = null
					this.searchParams.selectedAccount = null
					this.$global.setSelectedTenant({ target: { value: '' } })
					break
				case 'account':
					this.searchParams.selectedAccount = null
					break
				case 'carrier':
					this.searchParams.carrier = null
					this.searchParams.carrierName = null
					break
				case 'duration':
					this.searchParams.callDurationFrom = null
					this.searchParams.callDurationTo = null
					break
				case 'billableduration':
				case 'billable duration':
					this.searchParams.billableDurationFrom = null
					this.searchParams.billableDurationTo = null
					break
				case 'zone':
					this.searchParams.zoneNames = []
					break
				case 'source':
					this.searchParams.callFrom = ''
					break
				case 'destination':
					this.searchParams.callTo = ''
					break
				case 'callanywhere':
				case 'call anywhere':
					this.searchParams.callAnywhere = ''
					break
				case 'hangupcode':
				case 'hangup code':
					this.searchParams.hangupCodes = []
					break
				case 'withrecording':
				case 'with recording':
					this.searchParams.withRecording = null
					break
				case 'withtranscription':
				case 'with transcription':
					this.searchParams.withTranscription = null
					break
				case 'withvoicemail':
				case 'with voicemail':
					this.searchParams.withVoicemail = null
					break
			}

			this.searchParams = { ...this.searchParams }
			this.updateUrlParams()

			const searchFilters = this.$refs.searchFilters
			if (searchFilters) {
				searchFilters.initialiseFromParams(this.searchParams)
			}
		},
		removePresetFromUrl() {
			const url = new URL(window.location.href)
			url.searchParams.delete('preset')
			window.history.replaceState({}, '', url.pathname + (url.search ? url.search : ''))
		}
	}
}
</script>
